import React, {useCallback, useMemo, useState} from "react";
import { formatDate } from "~/utils/date";
import {isAfter} from "date-fns";

type UseScheduleStateProps = {
    responseStatus: "error" | "ok";
    errorDescription?: string;
    status: string;
    scheduleDate?: string;
    period?: string;
    address: string;
    organizationId?: number
};

type GetTitleAndDescriptionProps = { reschedule: boolean; canceled: boolean; afterJobDate: boolean } & UseScheduleStateProps;

const canCancelStatus = ["aberto", "em_andamento", "aguardando_confirmacao_agendamento", "agendamento_recusado", "agendamento_confirmado"];
const canRescheduleStatus = ["cancelado", ...canCancelStatus];
const publishedStatus = ["material_recebido", "material_disponibilizado", "finalizado_cliente"]

const labelPeriod = {
    manha: "Manhã",
    tarde: "Tarde",
    horario_comercial: "Horário comercial"
};

const isComercialHour = (date: Date): boolean => {
    const hour = date.getHours();
    return hour >= 8 && hour <= 19;
};

export const getLabelHour = (period: string, date: string) => {
    const parseDate = new Date(date);

    if (period) {
        return `${labelPeriod[period]}`;
    }

    if (!isComercialHour(parseDate)) {
        return labelPeriod.horario_comercial;
    }

        return formatDate(new Date(parseDate), "HH:mm");
    };


const getTitleAndDescription = ({ organizationId, responseStatus, errorDescription, status, canceled, reschedule, scheduleDate, period, address, afterJobDate }: GetTitleAndDescriptionProps) => {

    if (responseStatus === "error") {
        return {
            title: "Não foi possível carregar os dados de agendamento",
            description: (
                <>
                    <p>Desculpe, tivemos um problema para recuperar o seu job:</p>
                    <p>{errorDescription}</p>
                </>
            )
        }
    }

    if (status === "cancelado" || canceled) {
        return {
            title: "Reagende sua sessão!",
            description: <p>Sua sessão no endereço {address} foi cancelada, mas pode ser reagendada pelo botão abaixo:</p>
        };
    }

    if(publishedStatus.includes(status)){
        const descriptionText = organizationId === 80 ? <p>
            Sua sessão no endereço {address} já foi realizada e as fotos já estão disponíveis no seu anúncio!
            Você pode conferir o resultado pelo link que a SuaQuadra te enviou no Whatsapp
        </p> :
        <p>
            Sua sessão no endereço {address} já foi realizada e as fotos já estão disponíveis no painel do cliente!
        </p>

        return {
            title: "Sessão realizada!",
            description: descriptionText
        }
    }

    // Estou usando o canRescheduleStatus aqui pois ele possui todos os status que podemos fazer alguma ação
    if (!canceled && !canRescheduleStatus.includes(status) ) {
        return {
            title: "Este job não está mais disponível",
            description: <p>Este job não está mais disponível para modificação.
                <br />
                Caso deseje reagendar sua sessão, entre em contato conosco  clicando <a
                    href="https://wa.me/5511988721831"
                    target="_blank"
                    rel="noopener nofollow"
                    style={{ color: "inherit" }}
                >
                    aqui
                </a>.</p>
        }
    }

    if (afterJobDate && status === "em_andamento") {
        return {
            title: "Sessão em andamento!",
            description: <p>Sua sessão na {address} já está em andamento e não pode ser alterada.
                <br />
                Caso precise reagendar, entre em contato conosco clicando <a
                    href="https://wa.me/5511988721831"
                    target="_blank"
                    rel="noopener nofollow"
                    style={{ color: "inherit" }}
                >
                    aqui
                </a>.</p>
        };
    }

    // Estou usando o canRescheduleStatus aqui pois ele possui todos os status que podemos fazer alguma ação
    if (!canceled && !reschedule && !!scheduleDate && canRescheduleStatus.includes(status)) {
        return {
            title: "Sessão agendada!",
            description: (
                <p className="mb-4">
                    A sessão no endereço {address} está agendada para:{" "}
                    <strong>
                        {formatDate(new Date(scheduleDate), "dd/MM/yyyy")} - {getLabelHour(period, scheduleDate)}
                    </strong>
                </p>
            )
        }
    }

    return { title: "", description: "" };
};

export function useScheduleState({ responseStatus, errorDescription, status, scheduleDate, period, address, organizationId }: UseScheduleStateProps) {
    const [reschedule, setReschedule] = useState(status === "aguardando_agendamento");
    const [canceled, setCanceled] = useState(status === "cancelado");
    const [afterJobDate, ] = useState(isAfter(new Date(), new Date(scheduleDate)))


    const { title, description } = useMemo(() => {
        return getTitleAndDescription({ responseStatus, errorDescription, status, canceled, reschedule, scheduleDate, period, address, afterJobDate, organizationId });
    }, [status, canceled, reschedule, scheduleDate, period, responseStatus, errorDescription, address]);

    const markAsCanceled = useCallback(() => {
        setCanceled(true);
    }, []);

    const markToReschedule = useCallback(() => {
        setReschedule(true);
    }, [])


    return {
        title,
        description,
        markAsCanceled,
        markToReschedule,
        canceled,
        reschedule,
        isAfterJobDate: afterJobDate && status === 'em_andamento',
        canCancel: canCancelStatus.includes(status) && !canceled,
        canReschedule: canRescheduleStatus.includes(status),
        willReschedule: reschedule || status === "aguardando_agendamento"
    };
}
